import { type ReactNode, useRef, useState } from 'react'
import { TableCell, Tooltip, type TooltipProps } from '@mui/material'

interface OverflowCellProps {
  children: string
  /**
   * Shown on hover whether or not the text overflows, below the full text when it does. It
   * describes the cell rather than naming it, so the cell's accessible name stays its text.
   */
  tooltip?: ReactNode
  /** Where to show the tooltip relative to the cell; below it, MUI's default, when omitted. */
  tooltipPlacement?: TooltipProps['placement']
}

const tooltipTitle = (text: string, overflowing: boolean, tooltip: ReactNode) => {
  if (!overflowing) {
    return tooltip ?? ''
  }
  if (tooltip === undefined) {
    return text
  }
  return (
    <>
      {text}
      <br />
      {tooltip}
    </>
  )
}

/**
 * A `TableCell` that truncates long text with an ellipsis and shows a tooltip
 * on hover — but only when the content is actually overflowing, unless a
 * `tooltip` is given to show regardless.
 *
 * The parent `<Table>` should use `tableLayout: 'fixed'` so that columns have
 * constrained widths; without this, cells expand to fit content and truncation
 * won't activate.
 *
 * @example
 * ```tsx
 * <Table sx={{ tableLayout: 'fixed' }}>
 *   <TableBody>
 *     <TableRow>
 *       <OverflowCell>{user.email}</OverflowCell>
 *     </TableRow>
 *   </TableBody>
 * </Table>
 * ```
 */
export function OverflowCell({ children, tooltip, tooltipPlacement }: OverflowCellProps) {
  const cellRef = useRef<HTMLTableCellElement>(null)
  const [overflowing, setOverflowing] = useState(false)

  const handleMouseEnter = () => {
    const el = cellRef.current
    if (el) {
      setOverflowing(el.scrollWidth > el.clientWidth)
    }
  }

  return (
    <Tooltip
      title={tooltipTitle(children, overflowing, tooltip)}
      describeChild={tooltip !== undefined}
      disableInteractive
      placement={tooltipPlacement}
    >
      <TableCell
        ref={cellRef}
        sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        onMouseEnter={handleMouseEnter}
      >
        {children}
      </TableCell>
    </Tooltip>
  )
}
